import { AgentRun } from '@lucidtech/las-sdk-browser';
import { useNavigate } from '@tanstack/react-router';
import { flexRender, Row } from '@tanstack/react-table';
import { VirtualItem } from '@tanstack/react-virtual';
import { memo, useCallback, useMemo } from 'react';

import { merge } from '@/components';
import { useAgentRunTree } from '@/hooks';
import { TableRowProps } from '@/utils';

type RunsRowProps = {
  measureFn: (node: Element | null | undefined) => void;
  virtualRow: VirtualItem;
  row: Row<AgentRun>;
  runId: string;
  selected: boolean;
  agentId?: string;
  rowProps: TableRowProps | undefined;
  isFetching?: boolean;
};

const IRunsRow = ({ virtualRow, row, runId, selected, rowProps, measureFn, agentId, isFetching }: RunsRowProps) => {
  const { taskDocument } = useAgentRunTree({ agentId, runId });

  const navigate = useNavigate();

  const cells = useMemo(
    () =>
      row.getVisibleCells().map((cell) => (
        <td className="flex" key={cell.id} style={{ width: cell.column.getSize() }}>
          {flexRender(cell.column.columnDef.cell, cell.getContext())}
        </td>
      )),
    [row]
  );

  const style = useMemo(() => ({ transform: `translateY(${virtualRow.start}px)` }), [virtualRow.start]);

  const goToRun = useCallback(async () => {
    if (agentId) {
      await navigate({
        to: '/details/agents/$agentId/runs/$runId',
        params: { agentId: agentId, runId },
        search: { showPanel: 'agent-runs' },
      });
    }
  }, [navigate, agentId, runId]);

  const { className, ...rest } = rowProps ?? {};

  return (
    <tr
      className={merge(
        'group/row p-sm absolute flex w-full items-center border-b border-gray-200 bg-white hover:rounded-md hover:bg-gray-50',
        selected && 'bg-primary-50 rounded-md',
        taskDocument ? 'cursor-pointer' : 'cursor-not-allowed',
        isFetching && 'cursor-wait opacity-50 select-none',
        className
      )}
      data-index={virtualRow.index}
      ref={measureFn}
      key={virtualRow.index}
      style={style}
      onClick={taskDocument && !isFetching ? goToRun : undefined}
      {...rest}
    >
      {cells}
    </tr>
  );
};

export const RunsRow = memo(IRunsRow) as typeof IRunsRow;
